import { SimplifiedChampion } from '../../XXX/gameTypes';

/* ══════════════════ Waypoint & Path Segment ══════════════════ */

export interface Waypoint {
  gx: number;
  gy: number;
  /** If true, champion blinks here (ignores walls, within Flash range) */
  isFlash?: boolean;
}

export interface PathSegment {
  points: { x: number; y: number }[];
  isFlash: boolean;
  travelTimeSeconds: number;
}

/* ══════════════════ MapChampion ══════════════════ */

export interface MapChampion {
  champion: SimplifiedChampion;
  /** Grid position on the 512×512 walkability grid (null = not placed yet) */
  gridPosition: { gx: number; gy: number } | null;
  color: string;
  /** Ordered waypoints — last one is the final destination */
  waypoints: Waypoint[];
  /** One segment per consecutive pair (position→wp1, wp1→wp2, …) */
  pathSegments: PathSegment[];
  /** Total travel time across all segments (seconds) */
  travelTimeSeconds: number | null;
  /** Whether to show the path on the map */
  showPath: boolean;
  /** Whether pathfinding is in progress */
  pathLoading: boolean;
}

/* ══════════════════ Constants ══════════════════ */

export const CHAMPION_COLORS = [
  '#4a9eff', '#ff6b6b', '#50c878', '#ffb347', '#b19cd9',
  '#ff69b4', '#20b2aa', '#ffd700', '#ff6347', '#00ced1',
];

/** Flash range in game units (matches League's Flash spell) */
export const FLASH_RANGE_UNITS = 400;

/** Flash range in grid cells (512-grid over 16 000 game-unit map ≈ 12.8) */
export const FLASH_RANGE_GRID = FLASH_RANGE_UNITS / (16000 / 512);

/* ══════════════════ Helpers ══════════════════ */

export function createMapChampion(
  champ: SimplifiedChampion,
  colorIdx: number,
): MapChampion {
  return {
    champion: champ,
    gridPosition: null,
    color: CHAMPION_COLORS[colorIdx % CHAMPION_COLORS.length],
    waypoints: [],
    pathSegments: [],
    travelTimeSeconds: null,
    showPath: true,
    pathLoading: false,
  };
}

export function getChampionMS(mc: MapChampion): number {
  return mc.champion.stats?.totalStats?.movementSpeed ?? 325;
}

/** Final destination = last waypoint, or null */
export function getDestination(mc: MapChampion): Waypoint | null {
  return mc.waypoints.length > 0
    ? mc.waypoints[mc.waypoints.length - 1]
    : null;
}

/* ══════════════════ Wards ══════════════════ */

export interface Ward {
  id: string;
  gx: number;
  gy: number;
  type: 'stealth' | 'control';
  team: 'blue' | 'red';
}

/** Ward sight radius = 900 game units (matches League of Legends) */
export const WARD_VISION_RANGE_UNITS = 900;
export const WARD_VISION_RANGE_GRID = WARD_VISION_RANGE_UNITS / (16000 / 512); // ≈ 28.8
